import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ApiClient } from '../api';
import { bauartname, nachweisstandname } from '../aussenKlartext';
import { Geraetetafel } from './Geraetetafel';
import type { Geraetezeile } from './Geraetetafel';
import { EmptyState, ErrorState, LoadingState } from '../muster';

/**
 * Die Geräte der Außenanlagen — mit Fabrikatsansicht und öffentlichem Link.
 *
 * --- Was hier ein Gerät ist ------------------------------------------------
 *
 * Draußen gibt es keine Geräteliste im Modell. Was ein Gerät ist, ergibt sich
 * aus der Bauart: das Abwasserpumpwerk, zwei Fettabscheider, zwölf Hydranten
 * und die Versickerungsbauwerke. Ein Schacht dagegen ist keines — er hat kein
 * Fabrikat und keine Kennlinie, und 325 Schächte in dieser Liste machten sie
 * unbrauchbar.
 *
 * --- Der öffentliche Verweis -----------------------------------------------
 *
 * Derselbe Weg wie bei der Heizung: ein Link mit HMAC-Signatur, der ohne
 * Anmeldung geöffnet werden kann. Er wird ins Modell geschrieben und aufs
 * Typenschild geklebt — wer vor dem Pumpwerk steht, soll seine Daten sehen
 * können, ohne ein Konto zu haben. Die Gerätetafel zeigt ihn je Zeile.
 *
 * Die eigene öffentliche Seite `AussengeraetOeffentlich` stand bis TS9b1b
 * (26.10.2026) hier, war aber an keine Route gebunden und wurde nirgends
 * eingebunden - sie ist entfallen, mit ihr der Helfer `lesbar`.
 */

// Die drei Schnittstellen Bauwerk, Hydrant und Bauwerk2 stehen hier nicht
// mehr: Seit die Liste die gemeinsame Gerätetafel benutzt, gibt es EINE
// Zeilenform für alle Gewerke — Geraetezeile. Drei eigene Formen daneben
// hätten nur beschrieben, was der Endpunkt liefert, und wären beim ersten
// zusätzlichen Feld still veraltet.

/*
 * ART und Artname standen bis zum 23.09.2026 hier als zwei eigene
 * Teillisten - sechs bzw. sieben Bauarten, und 'ueberflur' fehlte in
 * beiden. Genau das war auf dem Bildschirm des Fachplaners zu sehen:
 * „Art: Hydrant ueberflur". Die Uebersetzung steht jetzt vollstaendig in
 * `../aussenKlartext`, gespiegelt aus `AussenKlartext.cs`.
 */

export function AussenGeraete({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const navigieren = useNavigate();
  const [g, setG] = useState<any | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.aussenGeraete(projektId)
      .then(setG)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  // Alle Arten in EINE Tafel — das Muster der Heizung.
  //
  // Vorher standen Bauwerke, Hydranten und Versickerungsbauwerke in drei
  // getrennten Tabellen. Das ist dreimal Suche und dreimal Filter, und wer
  // eine Kennung sucht, weiß vorher meist nicht, in welcher der drei sie
  // liegt.
  //
  // ═════════════════════════════════════════════════════════════════════
  // EIN FAMILIENSCHLÜSSEL IST KEIN FABRIKAT
  // ═════════════════════════════════════════════════════════════════════
  //
  // Bis zum 23.09.2026 stand in der Spalte „Fabrikat und Typ" der rohe
  // Revit-Familienname: `aue49610fl16wero10rd2bys3e21dw(400) ·
  // Überflurhydrant Typ 496 DN 100 1.00 m rot emailliert`. Das erste
  // Stück ist ein Familienschlüssel aus dem Katalog des Herstellers —
  // eine Zeichenfolge, die niemand liest und die in keiner Ausschreibung
  // steht. Das zweite ist der Typname, und DER ist die Angabe.
  //
  // Die Regel: Fabrikat = Hersteller + Modell, wenn beides belegt ist;
  // sonst der Revit-TYPNAME; sonst die Bauart im Klartext. Der
  // Familienschlüssel steht nur noch auf der Produktseite hinter
  // „Details" — nicht, weil er falsch wäre, sondern weil er in einer
  // Geräteliste nichts erklärt.
  const zeilen: Geraetezeile[] = useMemo(() => {
    if (!g) return [];

    const aus: Geraetezeile[] = [];

    // Die fachliche Kennung, oder die Elementkennung AUSGEWIESEN als
    // solche. Eine nackte Zahl in der Spalte „Kennung" liest sich wie
    // eine Anlagenkennzeichnung, und das ist sie nicht.
    const kennung = (
      fachlich: string | null | undefined, element: number | null | undefined,
    ): string | null => fachlich
      || (element == null ? null : `Element ${element}`);

    for (const b of g.bauwerke ?? [])
      aus.push({
        id: b.id,
        art: bauartname(b.bauart),
        hersteller: b.hersteller ?? null,
        modell: b.modell ?? null,
        bezeichnung: b.typ ?? bauartname(b.bauart),
        kennung: kennung(b.kennung, b.revitElementId), raum: b.bereich,
        kennwert: b.zulaufLs ? `${b.zulaufLs} l/s` : null,
        verweis: b.verweis,
        offen: b.kennung ? null : ['ohne fachliche Kennung'],
      });

    for (const h of g.hydranten ?? [])
      aus.push({
        id: h.id,
        art: bauartname(h.bauart),
        hersteller: h.hersteller ?? null,
        modell: h.modell ?? null,
        bezeichnung: h.typ ?? bauartname(h.bauart),
        kennung: kennung(h.kennung, h.revitElementId), raum: h.bereich,
        kennwert: h.abgabeLMin ? `${h.abgabeLMin} l/min` : null,
        verweis: h.verweis,
        offen: [
          h.kennung ? null : 'ohne fachliche Kennung',
          h.fliessdruckBar ? null : 'Fließdruck fehlt',
          h.befund && h.befund !== 'ok' ? h.befund : null,
        ].filter(Boolean) as string[],
      });

    for (const v of g.versickerung ?? [])
      aus.push({
        id: v.id,
        art: bauartname(v.bauart),
        hersteller: v.hersteller ?? null,
        modell: v.modell ?? null,
        bezeichnung: v.bezeichnung ?? v.typ ?? bauartname(v.bauart),
        kennung: kennung(v.bezeichnung, v.revitElementId),
        kennwert: v.nutzbarM3 ? `${v.nutzbarM3} m³` : null,
        verweis: v.verweis,
        offen: [
          v.nachweisstand && v.nachweisstand !== 'nachgewiesen'
            ? `Nachweis: ${nachweisstandname(v.nachweisstand)}` : null,
        ].filter(Boolean) as string[],
      });

    return aus;
  }, [g]);

  if (fehler) {
    return (
      <ErrorState
        meldung="Die Geräte der Außenanlagen konnten nicht geladen werden."
        technisch={fehler}
        beiErneutVersuchen={() => {
          setFehler(null);
          api.aussenGeraete(projektId)
            .then(setG)
            .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
        }}
      />
    );
  }

  if (!g) return <LoadingState text="Geräte der Außenanlagen werden geladen …" zeilen={8} spalten={5} />;

  if (zeilen.length === 0) {
    return (
      <EmptyState
        titel="Keine Geräte der Außenanlagen."
        erklaerung={'Ein Gerät ergibt sich aus der Bauart: Pumpwerk, Abscheider, Hydranten und '
          + 'Versickerungsbauwerke. Sie entstehen mit der Außenanlagenaufnahme („Modell lesen“); '
          + 'Schächte sind keine Geräte.'}
      />
    );
  }

  // Der Detailverweis geht auf die PRODUKTSEITE — dieselbe wie bei der
  // Heizung. Vorher zeichnete diese Datei eine eigene Detailseite; zwei
  // Darstellungen derselben Sache laufen auseinander, sobald eine
  // angefasst wird.
  return (
    <Geraetetafel
      zeilen={zeilen}
      kennwertName="Kennwert"
      aufDetail={(id) => navigieren(`../aussen-geraete/${id}`)}
    />
  );
}
